import React, { useEffect, useState } from "react";
import { useAuthenticatedApi } from "@/utils/callApi";
import { BrandButton as Button, Toast } from "@/components";
import HospiceSearchableSelect from "@/components/HospiceSearchableSelect";
import useToast from "@/components/useToast";

export default function ManageHospiceLists() {
  const { secureHelpers } = useAuthenticatedApi();
  const { toast, showToast, hideToast } = useToast();

  const [hospices, setHospices] = useState([]);
  const [lists, setLists] = useState([]);
  // Create form state
  const [newName, setNewName] = useState("");
  const [newHospiceIds, setNewHospiceIds] = useState([]);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editHospiceIds, setEditHospiceIds] = useState([]);

  const loadLists = async () => {
    try {
      const data = await secureHelpers.get(`/api/Hospices/Lists`);
      setLists(data || []);
      console.log('✳️ Loaded lists:', lists);
    } catch (error) {
      console.error('Failed to load lists:', error);
      showToast({ variant: 'error', message: 'Failed to load lists' });
    }
  };

  useEffect(() => {
    loadLists();
    loadHospices();
  }, []);

  const loadHospices = async () => {
    try {
      const data = await secureHelpers.get('/api/Hospices');
      const sorted = Array.isArray(data)
        ? data.filter(h => h && h.name).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        : [];
      setHospices(sorted);
    } catch (error) {
      console.error('Failed to load hospices:', error);
      showToast({ variant: 'error', message: 'Failed to load hospices' });
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || newHospiceIds.length === 0) return;
    const forApi = {
      Name: newName.trim(),
      Targets: newHospiceIds,
    };
    try {
      await secureHelpers.post(`/api/Hospices/Lists`, forApi);
      showToast({ variant: 'success', message: `List "${forApi.Name}" created` });
      setNewName("");
      setNewHospiceIds([]);
      await loadLists();
    } catch (error) {
      console.error('Failed to create list:', error);
      showToast({ variant: 'error', message: 'Failed to create list' });
    }
  };

  const handleDelete = async (id) => {
    const list = lists.find(l => l.id === id);
    try {
      await secureHelpers.delete(`/api/Hospices/Lists/${id}`);
      showToast({ variant: 'success', message: `List "${list?.name}" deleted` });
      await loadLists();
    } catch (error) {
      console.error('Failed to delete list:', error);
      showToast({ variant: 'error', message: 'Failed to delete list' });
    }
  };

  const startEdit = (list) => {
    setEditingId(list.id);
    setEditName(list.name);
    setEditHospiceIds([...list.targets]);
  };

  const saveEdit = async () => {
    if (!editName.trim() || editHospiceIds.length === 0) return;
    const forApi = {
      Id: editingId,
      Name: editName.trim(),
      Targets: editHospiceIds,
    };
    try {
      await secureHelpers.put(`/api/Hospices/Lists/${editingId}`, forApi);
      showToast({ variant: 'success', message: 'List updated' });
      setEditingId(null);
      setEditName("");
      setEditHospiceIds([]);
      await loadLists();
    } catch (error) {
      console.error('Failed to update list:', error);
      showToast({ variant: 'error', message: 'Failed to update list' });
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditHospiceIds([]);
  };

  const getHospiceName = (id) => {
    const h = hospices.find(h => h.id === id);
    return h?.name || id;
  };

  return (
    <div className="container-fluid py-4">
      <h1 style={{ color: "#F16D09", marginBottom: "2rem" }}>Manage Hospice Lists</h1>

      <Toast {...toast} onClose={hideToast} />

      <div className="row g-4 mb-4">
        <div className="col-md-12">
          <div className="card p-3 h-100">
            <p className="text-muted mb-4">
              Create named lists of hospices for quick selection when publishing surveys.
            </p>

            <div className="row g-4">
              {/* Create new list */}
              <div className="col-md-5">
                <div className="card p-4" style={{ backgroundColor: '#e7f3ff' }}>
                  <h5 className="mb-3">Create New List</h5>

                  <div className="mb-3">
                    <label className="form-label">List Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. London Region"
                      value={newName}
                      onChange={e => setNewName(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Hospices</label>
                    <HospiceSearchableSelect
                      hospices={hospices}
                      value={newHospiceIds}
                      onChange={setNewHospiceIds}
                      multiple={true}
                      placeholder="Search and select hospices..."
                    />
                  </div>

                  <Button
                    onClick={handleCreate}
                    disabled={!newName.trim() || newHospiceIds.length === 0}
                  >
                    Create List
                  </Button>
                </div>
              </div>

              {/* Existing lists */}
              <div className="col-md-7">
                <h5 className="mb-3">
                  Existing Lists
                  {lists.length > 0 && (
                    <span className="ms-2 bg-secondary">{lists.length}</span>
                  )}
                </h5>

                {lists.length === 0 ? (
                  <p className="text-muted">No lists yet. Create one using the form.</p>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {lists.map(list => (
                      <div key={list.id} className="card p-3">
                        {editingId === list.id ? (
                          <>
                            <div className="mb-3">
                              <label className="form-label">List Name</label>
                              <input
                                type="text"
                                className="form-control"
                                value={editName}
                                onChange={e => setEditName(e.target.value)}
                              />
                            </div>
                            <div className="mb-3">
                              <label className="form-label">Hospices</label>
                              <HospiceSearchableSelect
                                hospices={hospices}
                                value={editHospiceIds}
                                onChange={setEditHospiceIds}
                                multiple={true}
                                placeholder="Search and select hospices..."
                              />
                            </div>
                            <div className="d-flex gap-2">
                              <Button
                                onClick={saveEdit}
                                disabled={!editName.trim() || editHospiceIds.length === 0}
                                style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                              >
                                Save
                              </Button>
                              <Button
                                onClick={cancelEdit}
                                style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <h3 className="mb-0" style={{ color: '#F16D09' }}>{list.name}</h3>
                                <small className="text-muted">
                                  {list.targets.length} hospice{list.targets.length !== 1 ? 's' : ''}
                                </small>
                              </div>
                              <div className="d-flex gap-1">
                                <Button
                                  onClick={() => startEdit(list)}
                                  style={{ background: '#F16D09', padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  onClick={() => handleDelete(list.id)}
                                  style={{ background: '#dc3545', padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                            <div className="d-flex flex-wrap gap-1">
                              {list.targets.map(id => (
                                <span
                                  key={id}
                                  className="badge bg-light text-dark border"
                                  style={{ fontSize: "var(--font-heading-sm)" }}
                                >
                                  {getHospiceName(id)}
                                </span>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
